"use client";

import { useEffect, useState } from "react";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import { LOGIN_CALLBACK_ROUTE, useAuth0V2 } from "@stadium/services/auth0";

import TextInput from "@stadium/ui/src/components/form/TextInput";
import Button, { ExternalLinkButton } from "@stadium/shared/src/components/shared/button";

import { fetchGiftCodeDetailsByCode } from "@stadium/shared/src/apis/gift-codes";
import { unwrapPromise } from "@stadium/helpers/src/utility";

import {
  REDEEM_GIFT_CODE_BASE_ROUTE,
  getRedeemGiftCodeRoute,
} from "@stadium/shared/src/constants/routes";

import { GIFT_CODE_REDEMPTION_LEARN_MORE_URL } from "@stadium/shared/src/constants/redeem-gift-code";
import { BASE_PATH } from "@stadium/shared/src/constants/env-variables";
import { normalizeGiftCode } from "@stadium/shared/src/helpers/gift-code";

import styles from "./main.module.scss";

const ApplyGiftCode = () => {
  const router = useRouter();
  const { gc: initialGiftCode = "", autoApply = null } = router.query as {
    gc: string;
    autoApply: string | null;
  };

  const { isAuthenticated, loginWithRedirect } = useAuth0V2();

  const [giftCode, setGiftCode] = useState(initialGiftCode);
  const [applyError, setApplyError] = useState("");
  const [isApplying, setIsApplying] = useState(false);

  const handleFetchGiftCodeDetails = async (codeValue) => {
    setIsApplying(true);
    const [response, error] = await unwrapPromise(fetchGiftCodeDetailsByCode(codeValue));
    if (error) {
      const errorMessage = error?.response?.data?.errors?.[0]?.detail;
      setApplyError(errorMessage || "Unable to apply this gift code.");
      setIsApplying(false);
      return;
    }

    const attributes = response?.data?.attributes || {};
    if (!attributes?.number) {
      setApplyError("This code is not valid.");
      setIsApplying(false);
      return;
    }

    router.push(getRedeemGiftCodeRoute(attributes.number, codeValue));
    setIsApplying(false);
  };

  const handleApplyCode = async () => {
    const codeValue = normalizeGiftCode(giftCode);
    if (!codeValue) {
      setApplyError("Please enter a gift code.");
      return;
    }
    setApplyError("");

    if (!isAuthenticated) {
      loginWithRedirect({
        redirectUri: window.location.origin + BASE_PATH + LOGIN_CALLBACK_ROUTE,
        appState: { returnTo: `${REDEEM_GIFT_CODE_BASE_ROUTE}?gc=${giftCode}` },
      });
      return;
    }

    handleFetchGiftCodeDetails(codeValue);
  };

  useEffect(() => {
    if (autoApply) {
      handleApplyCode();
    }
  }, [autoApply]);

  const renderApplyCodeCta = () => {
    return (
      <Button
        type="submit"
        isLoading={isApplying}
        className={styles.applyCta}
        aria-busy={isApplying}
        disabled={isApplying}
      >
        {isApplying ? "Applying..." : "Apply Code"}
      </Button>
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleApplyCode();
  };

  return (
    <section
      className={`${styles.mainContainer} main-container-wrapper`}
      aria-labelledby="gift-code-heading"
    >
      <div className={`${styles.mainInnerContainer} ${styles.applyGiftCode}`}>
        <header id="gift-code-heading" className={styles.cardHeading} tabIndex={0}>
          Redeem Gift Code
        </header>
        <form onSubmit={handleSubmit} noValidate>
          <TextInput
            id="giftCode"
            prefix={<label htmlFor="giftCode">ENTER GIFT CODE</label>}
            value={giftCode}
            onChange={(e) => setGiftCode(normalizeGiftCode(e.value))}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            classes={styles.inputWrapper}
            error={!!applyError}
          />
          {applyError && <div className={styles.errorMessage}>{applyError}</div>}
          {renderApplyCodeCta()}
        </form>

        <ExternalLinkButton
          href={GIFT_CODE_REDEMPTION_LEARN_MORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          btnStyle="link"
          className={styles.learnCta}
          aria-label="Learn more about gift code redemption (opens in new window)"
        >
          Learn more
        </ExternalLinkButton>
      </div>
    </section>
  );
};

export default ApplyGiftCode;
